/**
 * InfosEventosWidget.tsx — 08/07/2026 (pendência S2 §2.3)
 * Espelha na Home as INFOs de evento que dependem do usuário:
 *   · estado "pendente"  → a área DONA precisa informar (ex.: Comercial)
 *   · estado "informado" → a área DESTINO precisa aplicar durante o evento (ex.: Conteúdo)
 * Coordenação/Diretoria sem área vinculada enxergam todas.
 * Clique leva ao Painel do evento (/eventos?painel=<id>).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronRight } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { Demanda, Evento } from '@/lib/types';

interface InfoPendente {
  demandaId: string;
  eventoId?: string;
  eventoNome: string;
  texto: string;
  acao: 'informar' | 'aplicar';
}

export function InfosEventosWidget({ demandas, eventos }: { demandas: Demanda[]; eventos: Evento[] }) {
  const { user } = useAuth();

  const pessoasQ = useQuery({
    queryKey: ['pessoas'],
    queryFn: () => api.pessoas.list().then((r) => r.data),
    staleTime: 60_000,
  });
  const minhasAreas = useMemo(() => {
    if (!user?.pessoaId) return [] as string[];
    return pessoasQ.data?.find((p) => p.id === user.pessoaId)?.areasIds ?? [];
  }, [pessoasQ.data, user?.pessoaId]);

  const gestorSemArea = (user?.papel === 'Coordenação' || user?.papel === 'Diretoria') && minhasAreas.length === 0;
  const evNome = useMemo(() => new Map(eventos.map((e) => [e.id, e.nome])), [eventos]);

  const infos = useMemo<InfoPendente[]>(() => {
    const out: InfoPendente[] = [];
    for (const d of demandas) {
      if (d.tipoDemanda !== 'card_mae' || !d.customChecklist) continue;
      let itens: { natureza?: string; estado?: string; texto?: string; areaDona?: string; areaDestino?: string }[] = [];
      try { itens = JSON.parse(d.customChecklist); } catch { continue; }
      for (const it of itens) {
        if (it?.natureza !== 'info' || it?.estado === 'aplicado') continue;
        const acao: InfoPendente['acao'] = it.estado === 'informado' ? 'aplicar' : 'informar';
        const areaResp = acao === 'aplicar' ? it.areaDestino : it.areaDona;
        if (!gestorSemArea && (!areaResp || !minhasAreas.includes(areaResp))) continue;
        out.push({
          demandaId: d.id,
          eventoId: d.eventoId,
          eventoNome: evNome.get(d.eventoId ?? '') ?? '—',
          texto: it.texto ?? '',
          acao,
        });
      }
    }
    return out.slice(0, 8);
  }, [demandas, minhasAreas, gestorSemArea, evNome]);

  if (infos.length === 0) return null;

  return (
    <div className="bg-yellow/5 border border-yellow/30 rounded-xl overflow-hidden">
      <div className="px-3 py-2.5 border-b border-yellow/20 flex items-center gap-2">
        <AlertCircle className="w-3.5 h-3.5 text-yellow" />
        <span className="text-xs font-bold">INFOs de evento aguardando você</span>
        <span className="text-[10px] text-muted">({infos.length})</span>
      </div>
      <div className="px-3 py-2 space-y-1">
        {infos.map((info, i) => (
          <Link
            key={`${info.demandaId}-${i}`}
            to={info.eventoId ? `/eventos?painel=${info.eventoId}` : '/eventos'}
            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-yellow/10 transition-colors group"
          >
            <span className={clsx(
              'text-[9px] px-1.5 py-0.5 rounded-full border font-bold uppercase shrink-0',
              info.acao === 'aplicar'
                ? 'bg-green/15 text-green border-green/40'
                : 'bg-yellow/15 text-yellow border-yellow/40',
            )}>
              {info.acao}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-medium truncate">{info.texto}</div>
              <div className="text-[10px] text-muted truncate">{info.eventoNome}</div>
            </div>
            <ChevronRight className="w-3 h-3 text-muted opacity-0 group-hover:opacity-100 shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
